import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import UserSettingsOptions, {
  V2_INSIGHTS_OVERVIEW_PATH,
  V2_INSIGHTS_OPERATIONS_PATH,
  V2_INSIGHTS_SALES_PATH,
  V2_INSIGHTS_CUSTOMERS_PATH,
  V2_INSIGHTS_PRODUCTS_PATH,
  V2_ORDERS_PATH,
  V2_SHIPMENTS_PATH,
  V2_PRODUCTS_PATH,
  V2_CUSTOMERS_PATH,
} from '../enums/user-settings/user-settings-options';
import NotificationService, { NOTIFICATION_TYPE_INFO } from 'ui-components/services/notification-service';

const PURCHASING_USER_ROLE = 'Purchasing';
const ORDER_MANAGEMENT_USER_ROLE = 'OrderManagement';
const SHIPPING_USER_ROLE = 'Shipping';
const PRODUCT_MANAGEMENT_USER_ROLE = 'ProductManagement';
const CUSTOMER_MANAGEMENT_USER_ROLE = 'CustomerManagement';
export const ADMINISTRATION_USER_ROLE = 'Administration';
export const CONFIGURATION_USER_ROLE = 'Configuration';
const SYSTEM_ADMINISTRATOR_USER_ROLE = 'SystemAdmin';
const REPORTING_USER_ROLE = 'Reporting';
const INVENTORY_MANAGEMENT_USER_ROLE = 'InventoryManagement';
const ACCOUNT_MANAGEMENT_USER_ROLE = 'AccountManagement';
const USER_INVENTORY_RESTRICTION = 'Inventory';
const USER_ORDER_VIEW_RESTRICTION = 'OrderView';
const USER_SHIPMENT_VIEW_RESTRICTION = 'ShipmentView';
const USER_QUICKSHIP_RESTRICTION = 'QuickShip';
const ORDER_PICKING_USER_ROLE = 'OrderPicking';
const MOBILE_PICKING_MANUAL_VERIFICATION_USER_ROLE = 'MobilePickingManualVerification';
const PACKING_SCAN_MANUAL_VERIFICATION_USER_ROLE = 'PackingScanManualVerification';
const TOTE_MANAGEMENT_CRUD_USER_ROLE = 'ToteManagementCrud';

const settingsRoute = '/settings';
const noPermissionsRoute = '/permission';
export const DEFAULT_ORDERS_PAGE_URL = '/orders/awaiting-shipment';
export const DEFAULT_SHIPMENTS_PAGE_URL = '/shipments/shipped-recent';
export const DEFAULT_PRODUCTS_PAGE_URL = '/products';
const DEFAULT_CUSTOMERS_PAGE_URL = '/customers';
const DEFAULT_INSIGHTS_PAGE_URL = '/dashboard';

export const v2LoginDirectlyToRoutesToV3Routes = {
  [V2_INSIGHTS_OVERVIEW_PATH]: {
    messageDescriptor: UserSettingsOptions.loginToInsightsOverview.messageDescriptor,
    requiredPermissionRole: REPORTING_USER_ROLE,
    route: DEFAULT_INSIGHTS_PAGE_URL,
  },
  [V2_INSIGHTS_OPERATIONS_PATH]: {
    messageDescriptor: UserSettingsOptions.loginToInsightsOperations.messageDescriptor,
    requiredPermissionRole: REPORTING_USER_ROLE,
    route: `${DEFAULT_INSIGHTS_PAGE_URL}/operations`,
  },
  [V2_INSIGHTS_SALES_PATH]: {
    messageDescriptor: UserSettingsOptions.loginToInsightsSalesTrends.messageDescriptor,
    requiredPermissionRole: REPORTING_USER_ROLE,
    route: `${DEFAULT_INSIGHTS_PAGE_URL}/sales`,
  },
  [V2_INSIGHTS_CUSTOMERS_PATH]: {
    messageDescriptor: UserSettingsOptions.loginToInsightsCustomerOverview.messageDescriptor,
    requiredPermissionRole: REPORTING_USER_ROLE,
    route: `${DEFAULT_INSIGHTS_PAGE_URL}/customers`,
  },
  [V2_INSIGHTS_PRODUCTS_PATH]: {
    messageDescriptor: UserSettingsOptions.loginToInsightsProductHighlights.messageDescriptor,
    requiredPermissionRole: REPORTING_USER_ROLE,
    route: `${DEFAULT_INSIGHTS_PAGE_URL}/products`,
  },
  [V2_ORDERS_PATH]: {
    messageDescriptor: UserSettingsOptions.loginToOrders.messageDescriptor,
    requiredPermissionRole: ORDER_MANAGEMENT_USER_ROLE,
    route: DEFAULT_ORDERS_PAGE_URL,
  },
  [V2_SHIPMENTS_PATH]: {
    messageDescriptor: UserSettingsOptions.loginToShipments.messageDescriptor,
    requiredPermissionRole: SHIPPING_USER_ROLE,
    route: DEFAULT_SHIPMENTS_PAGE_URL,
  },
  [V2_PRODUCTS_PATH]: {
    messageDescriptor: UserSettingsOptions.loginToProducts.messageDescriptor,
    requiredPermissionRole: PRODUCT_MANAGEMENT_USER_ROLE,
    route: DEFAULT_PRODUCTS_PAGE_URL,
  },
  [V2_CUSTOMERS_PATH]: {
    messageDescriptor: UserSettingsOptions.loginToCustomers.messageDescriptor,
    requiredPermissionRole: CUSTOMER_MANAGEMENT_USER_ROLE,
    route: DEFAULT_CUSTOMERS_PAGE_URL,
  },
};

const messages = defineMessages({
  notificationTitle: {
    defaultMessage: 'Your Permissions Have Changed',
    id: 'KYIHyB',
    description:
      'The title of the info notification explaining why the user that their permissions have changed limiting which pages they can see',
  },
  notificationMessage: {
    defaultMessage:
      'You no longer have permission to access the {pageName} screen. Go to your <link>Display Option settings</link> and update your selection for the <b>Log in directly to</b> option.',
    id: 'ilZVMZ',
    description:
      'Message in the notification body explaining why the user can no longer see the page that their "Login Directly To" display option is set to',
  },
});

const showLoginDirectlyToInfoNotification = (messageDescriptor) => {
  setTimeout(() => {
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_INFO,
      title: <FormattedMessage {...messages.notificationTitle} />,
      content: (
        <FormattedMessage
          {...messages.notificationMessage}
          values={{
            pageName: <FormattedMessage {...messageDescriptor} />,
            link: (chunks) => <Link to='/settings/displayoptions'>{chunks}</Link>,
          }}
        />
      ),
    });
  }, 0);
};

class UserPermissionService {
  constructor() {
    this.roleToDefaultRouteMap = {
      [ADMINISTRATION_USER_ROLE]: DEFAULT_ORDERS_PAGE_URL,
      [ORDER_MANAGEMENT_USER_ROLE]: DEFAULT_ORDERS_PAGE_URL,
      [SHIPPING_USER_ROLE]: DEFAULT_SHIPMENTS_PAGE_URL,
      [PRODUCT_MANAGEMENT_USER_ROLE]: DEFAULT_PRODUCTS_PAGE_URL,
      [CUSTOMER_MANAGEMENT_USER_ROLE]: DEFAULT_CUSTOMERS_PAGE_URL,
      [REPORTING_USER_ROLE]: DEFAULT_INSIGHTS_PAGE_URL,
      [ACCOUNT_MANAGEMENT_USER_ROLE]: settingsRoute,
      [CONFIGURATION_USER_ROLE]: settingsRoute,
      [PURCHASING_USER_ROLE]: settingsRoute,
      [INVENTORY_MANAGEMENT_USER_ROLE]: settingsRoute,
    };
  }

  userIsInRoleOrHasAdministrationPermission(userRoles, roleName) {
    if (!roleName) {
      return true;
    }
    if (!userRoles || !userRoles.length) {
      return false;
    }
    return userRoles.includes(roleName) || userRoles.includes(this.administrationPermissionRole);
  }

  userHasPurchasingRole(userRoles = []) {
    return this.userIsInRoleOrHasAdministrationPermission(userRoles, this.purchasingPermissionRole);
  }

  userHasOrderManagementRole(userRoles = []) {
    return this.userIsInRoleOrHasAdministrationPermission(userRoles, this.orderManagementPermissionRole);
  }

  userHasShippingRole(userRoles = []) {
    return this.userIsInRoleOrHasAdministrationPermission(userRoles, this.shippingPermissionRole);
  }

  userHasCustomerManagementRole(userRoles = []) {
    return this.userIsInRoleOrHasAdministrationPermission(userRoles, this.customerManagementPermissionRole);
  }

  userHasProductManagementRole(userRoles = []) {
    return this.userIsInRoleOrHasAdministrationPermission(userRoles, this.productManagementPermissionRole);
  }

  userHasInventoryManagementRole(userRoles = []) {
    return this.userIsInRoleOrHasAdministrationPermission(userRoles, this.inventoryManagementRole);
  }

  userHasConfigurationRole(userRoles = []) {
    return this.userIsInRoleOrHasAdministrationPermission(userRoles, this.configurationPermissionRole);
  }

  userHasAccountManagementRole(userRoles = []) {
    return this.userIsInRoleOrHasAdministrationPermission(userRoles, this.accountManagementPermissionRole);
  }

  userHasAdministrationRole(userRoles = []) {
    return this.userIsInRoleOrHasAdministrationPermission(userRoles, this.administrationPermissionRole);
  }

  // this is different from the normal user roles, and has a special carveout that doesn't
  // use the same userIsInRoleOrHasAdministrationPermission pattern as the others
  userIsSystemAdministrator(userRoles = []) {
    return userRoles.includes(this.systemAdministrationPermissionRole);
  }

  getFirstPermissableRouteUrl(userRoles = [], defaultUrl) {
    if (userRoles.length === 0) {
      return noPermissionsRoute;
    }
    if (defaultUrl) {
      const { route, requiredPermissionRole, messageDescriptor } = v2LoginDirectlyToRoutesToV3Routes[defaultUrl];
      if (route && this.userIsInRoleOrHasAdministrationPermission(userRoles, requiredPermissionRole)) {
        return route;
      }
      showLoginDirectlyToInfoNotification(messageDescriptor);
    }
    const intersection = userRoles.filter((x) => Object.keys(this.roleToDefaultRouteMap).includes(x));
    const firstRole = intersection[0];
    const routeByRole = this.roleToDefaultRouteMap[firstRole];
    if (!routeByRole) {
      return '/logout';
    }
    return routeByRole;
  }

  userHasOrderPickingRole(userRoles = []) {
    return this.userIsInRoleOrHasAdministrationPermission(userRoles, this.orderPickingPermissionRole);
  }

  userHasPackingScanManualVerificationRole(userRoles = []) {
    return this.userIsInRoleOrHasAdministrationPermission(userRoles, this.packingScanManualVerificationRole);
  }

  userHasToteManagementCrudRole(userRoles = []) {
    return this.userIsInRoleOrHasAdministrationPermission(userRoles, this.toteManagementCrudRole);
  }

  userHasRestriction(userRestrictions, restriction) {
    if (!restriction) {
      return false;
    }
    if (!userRestrictions || !userRestrictions.length) {
      return false;
    }
    return userRestrictions.includes(restriction);
  }

  userHasInventoryRestriction(userRestrictions = []) {
    return this.userHasRestriction(userRestrictions, this.inventoryRestriction);
  }

  userHasOrderViewRestriction(userRestrictions = []) {
    return this.userHasRestriction(userRestrictions, this.orderViewRestriction);
  }

  userHasShipmentViewRestriction(userRestrictions = []) {
    return this.userHasRestriction(userRestrictions, this.shipmentViewRestriction);
  }

  userHasQuickshipRestriction(userRestrictions = []) {
    return this.userHasRestriction(userRestrictions, this.quickshipRestriction);
  }

  uniqueUserIdAssignments(assignedUserIds = []) {
    return [...new Set(assignedUserIds)];
  }

  // Permissions
  get purchasingPermissionRole() {
    return PURCHASING_USER_ROLE;
  }

  get orderManagementPermissionRole() {
    return ORDER_MANAGEMENT_USER_ROLE;
  }

  get shippingPermissionRole() {
    return SHIPPING_USER_ROLE;
  }

  get productManagementPermissionRole() {
    return PRODUCT_MANAGEMENT_USER_ROLE;
  }

  get customerManagementPermissionRole() {
    return CUSTOMER_MANAGEMENT_USER_ROLE;
  }

  get administrationPermissionRole() {
    return ADMINISTRATION_USER_ROLE;
  }

  get configurationPermissionRole() {
    return CONFIGURATION_USER_ROLE;
  }

  get systemAdministrationPermissionRole() {
    return SYSTEM_ADMINISTRATOR_USER_ROLE;
  }

  get reportingPermissionRole() {
    return REPORTING_USER_ROLE;
  }

  get inventoryManagementRole() {
    return INVENTORY_MANAGEMENT_USER_ROLE;
  }

  get accountManagementPermissionRole() {
    return ACCOUNT_MANAGEMENT_USER_ROLE;
  }

  get orderPickingPermissionRole() {
    return ORDER_PICKING_USER_ROLE;
  }

  get mobilePickingManualVerificationRole() {
    return MOBILE_PICKING_MANUAL_VERIFICATION_USER_ROLE;
  }

  get packingScanManualVerificationRole() {
    return PACKING_SCAN_MANUAL_VERIFICATION_USER_ROLE;
  }

  get toteManagementCrudRole() {
    return TOTE_MANAGEMENT_CRUD_USER_ROLE;
  }

  get permissionOptions() {
    return [
      this.accountManagementPermissionRole,
      this.administrationPermissionRole,
      this.configurationPermissionRole,
      this.customerManagementPermissionRole,
      this.inventoryManagementRole,
      this.mobilePickingManualVerificationRole,
      this.orderManagementPermissionRole,
      this.orderPickingPermissionRole,
      this.packingScanManualVerificationRole,
      this.productManagementPermissionRole,
      this.purchasingPermissionRole,
      this.reportingPermissionRole,
      this.shippingPermissionRole,
      this.toteManagementCrudRole,
    ];
  }

  // Restrictions
  get inventoryRestriction() {
    return USER_INVENTORY_RESTRICTION;
  }

  get orderViewRestriction() {
    return USER_ORDER_VIEW_RESTRICTION;
  }

  get shipmentViewRestriction() {
    return USER_SHIPMENT_VIEW_RESTRICTION;
  }

  get quickshipRestriction() {
    return USER_QUICKSHIP_RESTRICTION;
  }

  get restrictionOptions() {
    return [this.inventoryRestriction, this.orderViewRestriction, this.shipmentViewRestriction, this.quickshipRestriction];
  }
}

export default new UserPermissionService();
